import { useCallback, useEffect, useState } from 'react'
import { CancelablePromise } from '../client/core/CancelablePromise'

type FetchFunction<T> = (params: {
    page: number
    limit: number
    search?: string
    sort?: string
    filters?: Record<string, string>
    createdAfter?: string
    createdBefore?: string
    uncontactedOnly?: boolean
    hasPreferredContactTime?: boolean
    preferredContactAfter?: string
    preferredContactBefore?: string
}) => CancelablePromise<T>
type CreateFunction<Q, R> = ({ requestBody }: { requestBody: Q }) => CancelablePromise<R>

/** The list state this hook drives: everything that shapes a request. */
export type PaginatedListState = {
    page: number
    limit: number
    sort: string
    search: string
    filters: Record<string, string>
}

export type PaginatedStatePatch = Partial<PaginatedListState> | ((previous: PaginatedListState) => Partial<PaginatedListState>)

/**
 * Lets a caller own the list state instead of this hook.
 *
 * Component-local `useState` dies with the component, so a list cannot survive
 * navigating into a detail page and back. Rather than change that for all eight
 * consumers, a caller can pass an adapter backed by somewhere longer-lived —
 * the URL, say — and everyone else keeps today's behaviour untouched.
 */
export type PaginatedStateAdapter = {
    state: PaginatedListState
    setState: (patch: PaginatedStatePatch) => void
}

const resolvePatch = (patch: PaginatedStatePatch, previous: PaginatedListState): Partial<PaginatedListState> =>
    typeof patch === 'function' ? patch(previous) : patch

export const usePaginatedData = <T, Q = {}, R = {}>(
    fetchFunction: FetchFunction<T>,
    initialPage = 1,
    initialLimit = 10,
    initialSort = '',
    initialFilters = {},
    createFunction?: CreateFunction<Q, R>,
    stateAdapter?: PaginatedStateAdapter
) => {
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    // Used only when no adapter is supplied. Declared unconditionally because
    // hooks cannot be called conditionally.
    const [internalState, setInternalState] = useState<PaginatedListState>({
        page: initialPage,
        limit: initialLimit,
        sort: initialSort,
        search: '',
        filters: initialFilters
    })

    const state = stateAdapter ? stateAdapter.state : internalState
    const adapterSetState = stateAdapter?.setState

    const setState = useCallback(
        (patch: PaginatedStatePatch) => {
            if (adapterSetState) {
                adapterSetState(patch)
                return
            }
            setInternalState((previous) => ({ ...previous, ...resolvePatch(patch, previous) }))
        },
        [adapterSetState]
    )

    const { page, limit, sort, search, filters } = state

    const setPage = useCallback((value: number) => setState({ page: value }), [setState])
    const setLimit = useCallback((value: number) => setState({ limit: value }), [setState])
    const setSort = useCallback((value: string) => setState({ sort: value }), [setState])
    const setSearch = useCallback((value: string) => setState({ search: value }), [setState])
    const setFilters = useCallback(
        (value: Record<string, string> | ((previous: Record<string, string>) => Record<string, string>)) =>
            setState(typeof value === 'function' ? (previous) => ({ filters: value(previous.filters) }) : { filters: value }),
        [setState]
    )

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            // Extract the new filter parameters
            const {
                createdAfter,
                createdBefore,
                uncontactedOnly,
                hasPreferredContactTime,
                preferredContactAfter,
                preferredContactBefore,
                ...otherFilters
            } = filters

            const response = await fetchFunction({
                page,
                limit,
                sort,
                ...otherFilters,
                search,
                createdAfter,
                createdBefore,
                uncontactedOnly: uncontactedOnly === 'true' ? true : uncontactedOnly === 'false' ? false : undefined,
                hasPreferredContactTime: hasPreferredContactTime === 'true' ? true : hasPreferredContactTime === 'false' ? false : undefined,
                preferredContactAfter,
                preferredContactBefore
            })
            setData(response)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred')
        } finally {
            setLoading(false)
        }
    }, [fetchFunction, page, limit, sort, filters, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const refetch = () => {
        fetchData()
    }

    const create = async (requestBody: Q) => {
        if (!createFunction) {
            throw new Error('Create function is not defined')
        }
        const response = await createFunction({ requestBody })
        refetch() // Re-fetch data after creating a new object
        return response
    }

    return {
        response: data,
        loading,
        error,
        page,
        setPage,
        limit,
        setLimit,
        sort,
        setSort,
        create: createFunction ? create : undefined,
        refetch,
        filters,
        setFilters,
        search,
        setSearch
    }
}
